import React, { useEffect, useState, useRef } from "react";
import { fetchCustomerwiseReport } from "../services/reportService";
import { ReportTable } from "../components/ReportTable";
import FilterBar from "../components/FilterBar";
import ExcelExport from "../components/ExcelExport";
import { reportColumns, hiddenRepeatKeys, groupKeyFields } from "../config/reportColumns";
import DashboardLayout from "../layouts/DashboardLayout";

const CustomerWiseReport = ({ onLogout }) => {
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // 🔹 Region (multi-select array)
    const [selectedRegion, setSelectedRegion] = useState([]);

    // 🔹 Territory (multi-select array)
    const [selectedTerritory, setSelectedTerritory] = useState([]);

    // 🔹 Party (multi-select array)
    const [selectedParty, setSelectedParty] = useState([]);

    // 🔹 Pack Size (multi-select array)
    const [selectedPackSize, setSelectedPackSize] = useState([]);

    const isFetchingRef = useRef(false);

    /* =============================
       LOAD REPORT - Only on Apply
    ============================== */
    const loadReport = async () => {
        if (isFetchingRef.current) return;

        if (!fromDate || !toDate) {
            setError("Please select both From and To dates");
            return;
        }

        isFetchingRef.current = true;

        try {
            setLoading(true);
            setError(null);

            // Reset all filters on new fetch
            setSelectedRegion([]);
            setSelectedTerritory([]);
            setSelectedParty([]);
            setSelectedPackSize([]);

            const res = await fetchCustomerwiseReport({
                fromDate,
                toDate,
            });

            setReportData(res.data || []);
        } catch (err) {
            setError("Failed to load report data");
            setReportData([]);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    };

    /* =============================
       REGION LIST
    ============================== */
    const regionList = React.useMemo(() => {
        if (!reportData.length) return [];

        const unique = [...new Set(
            reportData.map(row => row.region).filter(Boolean)
        )].sort();

        return unique.map(r => ({ label: r, value: r }));
    }, [reportData]);

    /* =============================
       FILTER BY REGION
    ============================== */
    const regionFilteredData = React.useMemo(() => {
        if (!selectedRegion.length) return reportData;
        return reportData.filter(row => selectedRegion.includes(row.region));
    }, [reportData, selectedRegion]);

    /* =============================
       TERRITORY LIST
    ============================== */
    const territoryList = React.useMemo(() => {
        if (!regionFilteredData.length) return [];

        const unique = [...new Set(
            regionFilteredData.map(row => row.territory).filter(Boolean)
        )].sort();

        return unique.map(t => ({ label: t, value: t }));
    }, [regionFilteredData]);

    /* =============================
       FILTER BY TERRITORY
    ============================== */
    const territoryFilteredData = React.useMemo(() => {
        if (!selectedTerritory.length) return regionFilteredData;
        return regionFilteredData.filter(row => selectedTerritory.includes(row.territory));
    }, [regionFilteredData, selectedTerritory]);

    /* =============================
       PARTY LIST
    ============================== */
    const partyList = React.useMemo(() => {
        if (!territoryFilteredData.length) return [];

        const unique = [...new Set(
            territoryFilteredData.map(row => row.party_name).filter(Boolean)
        )].sort();

        return unique.map(p => ({ label: p, value: p }));
    }, [territoryFilteredData]);

    /* =============================
       FILTER BY PARTY
    ============================== */
    const partyFilteredData = React.useMemo(() => {
        if (!selectedParty.length) return territoryFilteredData;
        return territoryFilteredData.filter(row => selectedParty.includes(row.party_name));
    }, [territoryFilteredData, selectedParty]);

    /* =============================
       PACK SIZE LIST
       (Table's "Pack Sizes" column = pack_sizes field)
    ============================== */
    const packSizeList = React.useMemo(() => {
        if (!partyFilteredData.length) return [];

        const unique = [...new Set(
            partyFilteredData.map(row => row.pack_sizes).filter(Boolean)
        )].sort();

        return unique.map(p => ({ label: p, value: p }));
    }, [partyFilteredData]);

    /* =============================
       FILTER BY PACK SIZE
    ============================== */
    const packSizeFilteredData = React.useMemo(() => {
        if (!selectedPackSize.length) return partyFilteredData;
        return partyFilteredData.filter(row => selectedPackSize.includes(row.pack_sizes));
    }, [partyFilteredData, selectedPackSize]);

    /* =============================
       SEARCH FILTER
    ============================== */
    const filteredReportData = React.useMemo(() => {
        if (!searchTerm) return packSizeFilteredData;

        const lower = searchTerm.toLowerCase();

        return packSizeFilteredData.filter(row =>
            Object.values(row).some(val =>
                val && val.toString().toLowerCase().includes(lower)
            )
        );
    }, [searchTerm, packSizeFilteredData]);

    /* =============================
       CASCADE RESET
    ============================== */
    useEffect(() => {
        setSelectedTerritory([]);
        setSelectedParty([]);
        setSelectedPackSize([]);
    }, [selectedRegion]);

    useEffect(() => {
        setSelectedParty([]);
        setSelectedPackSize([]);
    }, [selectedTerritory]);

    useEffect(() => {
        setSelectedPackSize([]);
    }, [selectedParty]);

    return (
        <DashboardLayout onLogout={onLogout}>
            <div className="flex flex-col h-screen w-full bg-gray-100 overflow-hidden">

                {/* HEADER */}
                <div className="bg-white px-4 py-3 border-b flex items-center justify-between shadow-sm z-30">
                    <h1 className="text-xl font-bold text-gray-800">
                        Party Wise Product Details Report
                    </h1>
                    <ExcelExport
                        data={filteredReportData}
                        columns={reportColumns}
                        reportTitle="Party Wise Product Details Report"
                        fileName="party_wise_product_report.xlsx"
                        reportFilters={{
                            fromDate,
                            toDate,
                            region: selectedRegion,
                            territory: selectedTerritory,
                            party: selectedParty,
                            packSize: selectedPackSize,
                            search: searchTerm
                        }}
                        // 🔹 Must mirror ReportTable's own grouping exactly
                        groupKeyFields={groupKeyFields}
                        // 🔹 Same columns ReportTable blanks/sums only on the first row of a group
                        hiddenRepeatKeys={[...hiddenRepeatKeys, "products_name"]}
                    />
                </div>

                {/* FILTER BAR */}
                <div className="bg-white px-4 py-2 border-b z-20 overflow-visible">
                    <FilterBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        fromDate={fromDate}
                        setFromDate={setFromDate}
                        toDate={toDate}
                        setToDate={setToDate}

                        regionList={territoryList}
                        selectedRegion={selectedTerritory}
                        setSelectedRegion={setSelectedTerritory}

                        territoryList={regionList}
                        selectedTerritory={selectedRegion}
                        setSelectedTerritory={setSelectedRegion}

                        partyList={partyList}
                        selectedParty={selectedParty}
                        setSelectedParty={setSelectedParty}

                        packSizeList={packSizeList}
                        selectedPackSize={selectedPackSize}
                        setSelectedPackSize={setSelectedPackSize}

                        onApply={loadReport}
                    />
                </div>

                {/* TABLE */}
                <div className="flex-1 overflow-auto bg-white z-10">
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-2"></div>
                            <p className="text-gray-500 italic">Fetching records...</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="text-center py-10 text-red-500 bg-red-50 m-4 rounded border border-red-200">
                            {error}
                        </div>
                    )}

                    {!loading && !error && reportData.length === 0 && (
                        <div className="text-center py-10 text-gray-500">
                            No data available. Please select a date range and click Apply.
                        </div>
                    )}

                    {!loading && !error && reportData.length > 0 && (
                        <ReportTable reportData={filteredReportData} />
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default CustomerWiseReport;